import { useEffect, useRef, useState, type PointerEvent, type MouseEvent } from 'react'

type Drag = { id: string; pointer: number; x: number; y: number; moving: boolean; source: HTMLElement; image?: string | null }
/** Snapshot the visible card, so the drag carries the exact media the user picked. */
export function calendarDragImage(source: HTMLElement): string | null {
  const image = source.querySelector('img')
  if (image) return image.currentSrc || image.getAttribute('src') || null
  const video = source.querySelector('video')
  if (!video || video.readyState < 2 || !video.videoWidth || !video.videoHeight) return null
  try {
    const canvas = document.createElement('canvas')
    canvas.width = 240; canvas.height = Math.max(1, Math.round(240 * video.videoHeight / video.videoWidth))
    const context = canvas.getContext('2d')
    if (!context) return null
    context.drawImage(video, 0, 0, canvas.width, canvas.height)
    return canvas.toDataURL('image/jpeg', .85)
  } catch { return null }
}

type Target = { day: string; beforeId?: string }

/** Internal calendar drags use pointer capture, including in the desktop app. */
export function useCalendarDrag(enabled: boolean, onDrop: (id: string, day: string, beforeId?: string) => void) {
  const root = useRef<HTMLDivElement>(null)
  const pending = useRef<Drag | null>(null)
  const suppressClick = useRef(false)
  const [preview, setPreview] = useState<{ id: string; x: number; y: number; image: string | null } | null>(null)
  const [day, setDay] = useState<string | null>(null)

  function targetAt(x: number, y: number): Target | null {
    const element = document.elementFromPoint(x, y)
    const cell = element?.closest<HTMLElement>('[data-calendar-day]')
    if (!cell?.dataset.calendarDay || !root.current?.contains(cell)) return null
    return { day: cell.dataset.calendarDay, beforeId: cell.closest('[data-calendar-order="time"]') ? undefined : element?.closest<HTMLElement>('[data-calendar-card]')?.dataset.calendarCard }
  }
  function clear() {
    const drag = pending.current
    pending.current = null
    if (drag?.source.hasPointerCapture(drag.pointer)) drag.source.releasePointerCapture(drag.pointer)
    setPreview(null); setDay(null)
  }
  useEffect(() => {
    const cancel = () => clear()
    const key = (event: KeyboardEvent) => { if (event.key === 'Escape') { suppressClick.current = Boolean(pending.current?.moving); clear() } }
    window.addEventListener('blur', cancel)
    window.addEventListener('keydown', key)
    return () => { window.removeEventListener('blur', cancel); window.removeEventListener('keydown', key) }
  }, [])

  useEffect(() => { if (!enabled) clear() }, [enabled])

  return {
    root, preview, day,
    handlers: {
      onPointerDown(event: PointerEvent<HTMLDivElement>) {
        suppressClick.current = false
        const source = (event.target as Element).closest<HTMLElement>('[data-calendar-clip]')
        if (!enabled || event.button !== 0 || !event.isPrimary || !source?.dataset.calendarClip) return
        pending.current = { id: source.dataset.calendarClip, pointer: event.pointerId, x: event.clientX, y: event.clientY, moving: false, source }
      },
      onPointerMove(event: PointerEvent<HTMLDivElement>) {
        const drag = pending.current
        if (!drag || drag.pointer !== event.pointerId) return
        if (!drag.moving && Math.hypot(event.clientX - drag.x, event.clientY - drag.y) < 6) return
        if (!drag.moving) { drag.moving = true; drag.image = calendarDragImage(drag.source); drag.source.setPointerCapture(event.pointerId) }
        event.preventDefault()
        setPreview({ id: drag.id, x: event.clientX, y: event.clientY, image: drag.image || null })
        setDay(targetAt(event.clientX, event.clientY)?.day || null)
      },
      onPointerUp(event: PointerEvent<HTMLDivElement>) {
        const drag = pending.current
        if (!drag || drag.pointer !== event.pointerId) return
        const target = drag.moving ? targetAt(event.clientX, event.clientY) : null
        suppressClick.current = drag.moving
        clear()
        if (enabled && target && target.beforeId !== drag.id) onDrop(drag.id, target.day, target.beforeId)
      },
      onPointerCancel: clear,
      onLostPointerCapture: clear,
      onClickCapture(event: MouseEvent<HTMLDivElement>) {
        if (suppressClick.current) { event.preventDefault(); event.stopPropagation(); suppressClick.current = false }
      },
    },
  }
}
